import React from 'react'
import { useTranslation } from 'react-i18next'
import { Camera } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useRoute } from '@/lib/RouteContext'
import { useMemberEntry } from '@/lib/hooks/useMemberEntry'
import { isPricingPath } from '@/lib/checkout/pricingPath'
import { cn } from '@/lib/utils'
import { useCompleteProfileOpen } from '@/components/features/members/completeProfileState'
import { ROUTES } from '@/lib/routes'

/** "Not now" keeps the bar away this long, per member and community. */
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000

/* System pages (`/_/…`: checkout confirmation, e-mail links, MCP consent) and
   the purchase flow are about one task; the bar would only stand in the way. */
const QUIET_PATHS = ['/products', '/checkout-success']
const isQuietPath = (path: string) =>
  path.startsWith('/_/') || isPricingPath(path) || QUIET_PATHS.includes(path)

const snoozeKey = (communityId: string, memberId: string) => `memberfy:profilePhotoBar:${communityId}:${memberId}`

const isSnoozed = (key: string): boolean => {
  try {
    const until = Number(localStorage.getItem(key))
    return Number.isFinite(until) && until > Date.now()
  } catch {
    return false
  }
}

/**
 * Invites someone without a photo in this community to complete their profile:
 * a soft bar under the header, never a modal. It goes away the moment a photo
 * is uploaded; "Not now"
 * hides it for 7 days on this device.
 */
export function ProfilePhotoBar({ className }: { className?: string }) {
  const { t } = useTranslation('account')
  const { user, activeMember: member, isRoleResolved, mustChangePassword } = useAuth()
  const { community } = useCommunity()
  const { currentPath, navigate } = useRoute()
  const { suspended, reason } = useMemberEntry()
  const completeProfileOpen = useCompleteProfileOpen()
  const [snoozedKey, setSnoozedKey] = React.useState<string | null>(null)
  const key = community?.id && member?.id ? snoozeKey(community.id, member.id) : null
  const hasPhoto = Boolean(member?.avatarUrl || user?.avatar)
  const path = (currentPath || '').split('?')[0]

  const visible = Boolean(user && isRoleResolved && member && key)
    && !hasPhoto
    && !(suspended && reason) // F-86: the red suspension banner speaks alone.
    && !completeProfileOpen
    && !mustChangePassword
    && !isQuietPath(path)
    && snoozedKey !== key
    && !isSnoozed(key as string)

  /* "Editar perfil" is Minha conta › Perfil, where the photo is the first thing (2026-10-29). */
  const openProfile = () => navigate(ROUTES.accountProfile)

  const snooze = () => {
    if (!key) return
    try {
      localStorage.setItem(key, String(Date.now() + SNOOZE_MS))
    } catch {
      /* Storage unavailable: it stays hidden until the next load. */
    }
    setSnoozedKey(key)
  }

  return (
    <>
      {visible && (
        <div
          role="region"
          aria-label={t('photoBar.title')}
          data-testid="profile-photo-bar"
          className={cn('border-b border-amber-200 bg-amber-50 px-4 py-3 sm:px-6', className)}
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <Camera className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">{t('photoBar.title')}</p>
                <p className="text-sm text-amber-800">{t('photoBar.body')}</p>
              </div>
            </div>
            <div className="flex flex-shrink-0 gap-2 pl-12 sm:pl-0">
              <Button type="button" variant="ghost" size="sm" className="flex-1 text-amber-900 hover:bg-amber-100 sm:flex-none" onClick={snooze}>
                {t('photoBar.later')}
              </Button>
              <Button type="button" variant="secondary" size="sm" className="flex-1 sm:flex-none" onClick={openProfile}>
                {t('photoBar.cta')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
